import React, { useCallback, useEffect, useRef } from 'react';
import { MapBrowserEvent } from 'ol';
import {
  cursorDragHandler,
  cursorMoveHandler,
  cursorUpHandler,
  cursorVectorTileHover,
} from '../../../../../apps/ais/src/modules/Map/mapCursors';
import { LayerItem } from './types';
import Map from 'ol/Map';
import { FeatureLike } from 'ol/Feature';
import { throttle } from 'lodash';
import Layer from 'ol/layer/Layer';
import { DoubleClickZoom } from 'ol/interaction';
import GeoJSON from 'ol/format/GeoJSON';

interface HoverState {
  feature: FeatureLike | null;
  position: { x: number; y: number } | null;
}

const dispatchMouseOutEvent = (element: HTMLElement, featureId: string) => {
  const event = new CustomEvent('layer-mouseout', {
    detail: { previousFeatureId: featureId },
  });
  element.dispatchEvent(event);
};

const dispatchMouseOverEvent = (
  element: HTMLElement,
  data: {
    properties: Record<string, any>;
    position: { x: number; y: number };
  }
) => {
  const event = new CustomEvent('layer-mouseover', {
    detail: data,
  });
  element.dispatchEvent(event);
};

const getLayerProperties = (layer: Layer | null) => {
  if (!layer) return null;
  try {
    return layer.get('properties') || null;
  } catch (error) {
    console.warn('Error getting layer properties:', error);
    return null;
  }
};

export const useMapLayerHandler = (
  map: Map,
  isDrawing: boolean,
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>,
  vectorTileLayers: LayerItem[],
  onVectorTileHover: (state: HoverState) => void,
  dataProjection: string
) => {
  // Track the currently hovered feature to prevent duplicate events
  const currentHoveredFeature = useRef<string | null>(null);

  const handleSingleClick = useCallback(
    (event: MapBrowserEvent<PointerEvent>) => {
      map.forEachFeatureAtPixel(
        event.pixel,
        (feature, layer) => {
          if (!feature || !layer) return false;

          const layerProps = getLayerProperties(layer);
          const isClickable = layerProps?.clickable;

          if (isClickable) {
            const properties = feature.getProperties();
            const mapElement = map.getTargetElement();

            if (!mapElement) return false;

            const clientPosition = {
              x: event.originalEvent.clientX,
              y: event.originalEvent.clientY,
            };

            const mapCoordinates = event.coordinate;

            const customEvent = new CustomEvent('layer-click', {
              detail: {
                properties,
                isCtrlClick: event.originalEvent.ctrlKey,
                position: clientPosition,
                coordinate: mapCoordinates,
              },
            });
            mapElement.dispatchEvent(customEvent);

            return true;
          }
          return false;
        },
        { hitTolerance: 10 }
      );
    },
    [map]
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      const pixel = map.getEventPixel(event);

      map.forEachFeatureAtPixel(
        pixel,
        (feature, layer) => {
          if (!feature || !layer) return false;

          const layerProps = getLayerProperties(layer);
          // Use contextMenuEnabled for right-click, fall back to clickable for backward compatibility
          const isContextMenuEnabled = layerProps?.contextMenuEnabled ?? layerProps?.clickable;

          if (isContextMenuEnabled) {
            // Prevent default browser context menu
            event.preventDefault();

            const properties = feature.getProperties();
            const mapElement = map.getTargetElement();

            if (!mapElement) return false;

            const clientPosition = {
              x: event.clientX,
              y: event.clientY,
            };

            // Extract geometry from feature and convert to GeoJSON format in data projection
            const geometry = feature.getGeometry();
            let geometryGeoJson = null;
            if (geometry && 'getType' in geometry) {
              const format = new GeoJSON();
              geometryGeoJson = format.writeGeometryObject(geometry as import('ol/geom/Geometry').default, {
                featureProjection: map.getView().getProjection(),
                dataProjection: dataProjection,
              });
            }

            const customEvent = new CustomEvent('layer-contextmenu', {
              detail: {
                properties,
                geometry: geometryGeoJson,
                position: clientPosition,
              },
            });
            mapElement.dispatchEvent(customEvent);

            return true;
          }
          return false;
        },
        { hitTolerance: 10 }
      );
    },
    [map, dataProjection]
  );

  const handleFeatureHover = useCallback(
    (event: MapBrowserEvent<PointerEvent>) => {
      let hoveredFeatureId: string | null = null;
      const mapElement = map.getTargetElement();

      if (!mapElement) return;

      // Find first hoverable feature at pixel
      map.forEachFeatureAtPixel(
        event.pixel,
        (feature, layer) => {
          if (!feature || !layer) return false;

          const layerProps = getLayerProperties(layer);
          const isHoverEnabled = layerProps?.hoverEnabled;

          if (!isHoverEnabled) return false;

          const properties = feature.getProperties();
          hoveredFeatureId = properties.id || null;

          // If we're still hovering the same feature, do nothing
          if (hoveredFeatureId === currentHoveredFeature.current) return true;

          // Handle mouseout for previous feature if it exists
          if (currentHoveredFeature.current) {
            dispatchMouseOutEvent(mapElement, currentHoveredFeature.current);
          }

          // Handle mouseover for new feature
          dispatchMouseOverEvent(mapElement, {
            properties,
            position: {
              x: event.originalEvent.clientX,
              y: event.originalEvent.clientY,
            },
          });

          currentHoveredFeature.current = hoveredFeatureId;
          return true;
        },
        { hitTolerance: 10 }
      );

      // If we're not hovering any feature but were previously, trigger mouseout
      if (!hoveredFeatureId && currentHoveredFeature.current) {
        dispatchMouseOutEvent(mapElement, currentHoveredFeature.current);
        currentHoveredFeature.current = null;
      }
    },
    [map]
  );

  const groupedCursorMoveHandler = throttle(
    (
      event: MapBrowserEvent<PointerEvent>,
      map: Map,
      canvas: HTMLCanvasElement | null,
      isDrawing: boolean,
      vectorTileLayers: LayerItem[],
      onVectorTileHover: (state: HoverState) => void
    ) => {
      if (isDrawing) {
        cursorMoveHandler(event, map, canvas, isDrawing);
        return;
      }

      handleFeatureHover(event);

      // First check for vector tile interactions
      const hasVectorTileHit = cursorVectorTileHover(
        event,
        map,
        vectorTileLayers,
        onVectorTileHover
      );
      // If no vector tile was hit, proceed with default cursor move handling
      if (!hasVectorTileHit) {
        cursorMoveHandler(event, map, canvas, isDrawing);
      }
    },
    32
  );

  useEffect(() => {
    const interactions = map.getInteractions().getArray();

    const doubleClickZoom = interactions.find(
      (interaction) => interaction instanceof DoubleClickZoom
    );

    if (doubleClickZoom) {
      // Disable double click zoom when drawing, enable it otherwise
      doubleClickZoom.setActive(!isDrawing);
    } else {
      console.warn(
        'DoubleClickZoom interaction not found, interactions',
        interactions
      );
    }

    return () => {
      // Re-enable double click zoom when component unmounts
      if (doubleClickZoom) {
        doubleClickZoom.setActive(true);
      }
    };
  }, [map, isDrawing]);

  useEffect(() => {
    const handleCursorMove = (event: MapBrowserEvent<PointerEvent>) =>
      groupedCursorMoveHandler(
        event,
        map,
        canvasRef.current,
        isDrawing,
        vectorTileLayers,
        onVectorTileHover
      );
    const handleCursorDrag = (event: MapBrowserEvent<PointerEvent>) =>
      cursorDragHandler(event, map);
    const handleCursorUp = (event: MapBrowserEvent<PointerEvent>) =>
      cursorUpHandler(event as MapBrowserEvent<PointerEvent>, map, isDrawing);

    map.on('pointermove', handleCursorMove);
    map.on('pointerdrag', handleCursorDrag);
    map.on('pointerup' as any, handleCursorUp);
    map.on('singleclick', handleSingleClick);

    // Add context menu listener to the map viewport
    const mapElement = map.getTargetElement();
    if (mapElement) {
      mapElement.addEventListener('contextmenu', handleContextMenu);
    }

    return () => {
      map.un('pointermove', handleCursorMove);
      map.un('pointerdrag', handleCursorDrag);
      map.un('pointerup' as any, handleCursorUp);
      map.un('singleclick', handleSingleClick);
      
      if (mapElement) {
        mapElement.removeEventListener('contextmenu', handleContextMenu);
      }
    };
  }, [
    map,
    canvasRef,
    handleSingleClick,
    handleContextMenu,
    isDrawing,
    vectorTileLayers,
    onVectorTileHover,
    groupedCursorMoveHandler,
  ]);
};
